import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  usuariosService,
  type DadosCadastroPublico,
  type DadosCriarUsuarioAdmin,
} from "@/core/services/usuariosService";
import { useAuth } from "@/core/hooks/useAuth";
import type { Database } from "@/shared/types/database.types";

type PessoaUpdate = Database["public"]["Tables"]["pessoas"]["Update"];

const QUERY_KEY_USUARIOS = ["usuarios"];

export function useUsuarios() {
  return useQuery({
    queryKey: QUERY_KEY_USUARIOS,
    queryFn: usuariosService.listarTodos,
  });
}

export function useUsuariosPendentes() {
  return useQuery({
    queryKey: [...QUERY_KEY_USUARIOS, "pendentes"],
    queryFn: usuariosService.listarPendentes,
    refetchInterval: 30_000, // pendentes merecem atualização um pouco mais frequente
  });
}

export function useCadastroPublico() {
  return useMutation({
    mutationFn: ({ dados, codigoIgreja }: { dados: DadosCadastroPublico; codigoIgreja?: string }) =>
      usuariosService.cadastrarPublico(dados, codigoIgreja),
  });
}

/**
 * Igreja do link de cadastro (`/cadastro/<codigo>`). Sem código só resolve
 * enquanto a plataforma tem UMA igreja ativa. Pública (roda antes do login).
 */
export function useIgrejaDoCadastro(codigo: string | undefined) {
  return useQuery({
    queryKey: ["igreja-do-cadastro", codigo ?? ""],
    queryFn: () => usuariosService.igrejaDoCadastro(codigo),
    retry: false,
    staleTime: 5 * 60_000,
  });
}

export function useCriarUsuarioAdmin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dados: DadosCriarUsuarioAdmin) => usuariosService.criarComoAdmin(dados),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY_USUARIOS }),
  });
}

export function useAtualizarUsuario() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: PessoaUpdate }) =>
      usuariosService.atualizar(id, dados),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY_USUARIOS }),
  });
}

/**
 * Meu perfil: a pessoa salva o próprio telefone/nascimento. Depois recarrega o cadastro da sessão
 * (`recarregarAcessos`) para a tela e o resto do app já mostrarem o valor novo.
 */
export function useAtualizarMeusDados() {
  const queryClient = useQueryClient();
  const { recarregarAcessos } = useAuth();
  return useMutation({
    mutationFn: ({
      id,
      telefone,
      dataNascimento,
    }: {
      id: string;
      telefone: string | null;
      dataNascimento: string;
    }) => usuariosService.atualizarMeusDados(id, { telefone, dataNascimento }),
    onSuccess: async () => {
      await recarregarAcessos();
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY_USUARIOS });
    },
  });
}

export function useAprovarUsuario() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => usuariosService.aprovar(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY_USUARIOS }),
  });
}

export function useInativarUsuario() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => usuariosService.inativar(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY_USUARIOS }),
  });
}

export function useReativarUsuario() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => usuariosService.reativar(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY_USUARIOS }),
  });
}
